function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { forwardRef, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { IconButton } from '../buttons';
import { Block, FlexRow, LargeText } from '../content';
import { Form } from '../forms';
import { FiltersIcon } from '../icons';
import { ProfileHeadDiv } from './Components';
import DownloadsMenu from './DownloadsMenu';
import FilterPopover from './FilterPopover';
import HeaderZone, { useL2Present } from './HeaderZone';
import OverflowActions from './OverflowActions';
import { useProviderSearch } from './ProviderSearchContext';

// Recursively collect form-field elements (anything with a `name`) from the <Filters>
// children, however deeply the page nests them in wrapper divs. Lets the L2 chrome pull
// the real search + filter inputs out of a page's bespoke layout and present them
// uniformly, ignoring the page's own filter toggle/badge chrome.
const collectNamedFields = nodes => {
  const out = [];
  React.Children.forEach(nodes, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return;
    }
    if (typeof child.props?.name === 'string' && child.props.name) {
      out.push(child);
      return;
    }
    if (child.props?.children) {
      out.push(...collectNamedFields(child.props.children));
    }
  });
  return out;
};
const ContainerHeaderCustom = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    t
  } = useTranslation();
  // When the deal provider L2 SubHeader chrome is present, relocate search/filters/actions
  // there; otherwise (feel, deal admin) keep the original inline layout.
  const l2Present = useL2Present();
  // When the search term is lifted to L1, drop the in-form search field and tell the chrome
  // this page has a search so it can show the L1 search box.
  const {
    active: searchLifted,
    setHasSearch,
    setHasFilters,
    setTerm
  } = useProviderSearch();
  const {
    entity,
    title,
    toolbarActionsContent,
    onChange,
    onSubmit,
    titleAndToolbars = false,
    onlyTitle = false,
    onlyToolbars = false,
    noHeader = false,
    titleAndFilters = false,
    children,
    showDownloadTool,
    showAiTool,
    onAiClick,
    searchFieldName = 'search',
    extraButton = null,
    paidPackage,
    hideTitle = false,
    filterCount = 0,
    onPrintClick
  } = props;
  const deepFields = collectNamedFields(children);
  const pageHasSearchField = deepFields.some(x => x.props?.name === searchFieldName);
  useEffect(() => {
    if (searchLifted && pageHasSearchField) {
      setHasSearch(true);
      return () => setHasSearch(false);
    }
    return undefined;
  }, [searchLifted, pageHasSearchField, setHasSearch]);

  // Whenever this page's form is portaled into the L2 SubHeader zone (below), SubHeader
  // has content to show — register it so SubHeader's auto-hide (see SubHeader.js) knows
  // not to collapse, matching the l2Present branch's own render condition.
  useEffect(() => {
    if (l2Present) {
      setHasFilters(true);
      return () => setHasFilters(false);
    }
    return undefined;
  }, [l2Present, setHasFilters]);

  // Automatically hide duplicate titles on Business & Provider routes because they have AdminHeader now
  const pathname = window.location.pathname;
  const isBusinessOrProviderRoute = pathname.includes('/management/') || pathname.includes('/providers/');
  const shouldHideTitle = hideTitle || isBusinessOrProviderRoute;
  if (noHeader) {
    return null;
  }
  if (onlyToolbars) {
    const isPortalOnly = toolbarActionsContent?.props?.children?.type?.render?.name === 'AdminHeaderPortal' || toolbarActionsContent?.props?.children?.type?.name === 'AdminHeaderPortal';
    if (isPortalOnly) {
      return /*#__PURE__*/React.createElement("div", {
        style: {
          padding: 0,
          margin: 0,
          height: 0
        }
      }, toolbarActionsContent);
    }
    return /*#__PURE__*/React.createElement(ProfileHeadDiv, null, /*#__PURE__*/React.createElement("div", null, toolbarActionsContent));
  }
  if (onlyTitle) {
    if (shouldHideTitle) {
      return null;
    }
    return /*#__PURE__*/React.createElement(FlexRow, {
      xs: 12,
      padding: 4
    }, /*#__PURE__*/React.createElement(LargeText, null, title || t(`${entity}.titlePlural`)));
  }
  const formProps = {
    onChange,
    onSubmit
  };
  if (titleAndFilters) {
    return /*#__PURE__*/React.createElement(Form, formProps, !shouldHideTitle && /*#__PURE__*/React.createElement(LargeText, null, title || t(`${entity}.titlePlural`)), /*#__PURE__*/React.createElement(FlexRow, null, children));
  }
  const _children = React.Children.toArray(children);
  const searchField = _children.find(x => x.props?.name === searchFieldName);
  const fields = _children.filter(x => x.props?.name !== searchFieldName);
  let content = null;
  if (!titleAndToolbars) {
    content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FlexRow, {
      justifyContent: fields > 1 && `space-around`,
      alignItems: "baseline"
    }, fields.map((x, i) => /*#__PURE__*/React.createElement(Fragment, {
      key: x.props.name || i
    }, x)), formProps.onSubmit && /*#__PURE__*/React.createElement(IconButton, {
      type: "submit",
      tooltip: t('GENERAL.applyFilters'),
      icon: FiltersIcon
    }), extraButton && extraButton, showDownloadTool && /*#__PURE__*/React.createElement(DownloadsMenu, {
      ref: ref,
      extraButton: extraButton,
      paidPackage: paidPackage,
      onPrintClick: onPrintClick
    }), showAiTool && /*#__PURE__*/React.createElement(IconButton, {
      tooltip: "AI Chat",
      onClick: onAiClick
    }, "AI")));
  }
  const showSearchField = !!searchField;

  // ── L2 layout (deal provider): the WHOLE form is portaled (as one unit, so DOM-based
  // value collection keeps working) into the L2 start zone. Inside: search (left), filter
  // dropdowns behind a "Filtri" popover (a FlexRow / MUI Grid so SelectOption xs sizing is
  // correct; panel is an absolute DOM child of the form, never a separate portal), then the
  // framework buttons. Page toolbars that self-portal still land in #admin-header-actions.
  if (l2Present) {
    // Deep-collected fields, so wrapper divs / the page's own filter toggle are ignored.
    // The search is rendered in L1 (searchLifted) — drop it here but keep it mounted
    // (hidden) so the form DOM still has it; the dropdowns go in the Filtri popover.
    const l2Search = deepFields.find(x => x.props?.name === searchFieldName);
    const l2Fields = deepFields.filter(x => x.props?.name !== searchFieldName);
    const frameworkActions = [extraButton, showDownloadTool && /*#__PURE__*/React.createElement(DownloadsMenu, {
      key: "dl",
      ref: ref,
      extraButton: extraButton,
      paidPackage: paidPackage,
      onPrintClick: onPrintClick
    }), showAiTool && /*#__PURE__*/React.createElement(IconButton, {
      key: "ai",
      tooltip: "AI Chat",
      onClick: onAiClick
    }, "AI")].filter(Boolean);
    return /*#__PURE__*/React.createElement(HeaderZone, {
      targetId: "admin-header-start"
    }, /*#__PURE__*/React.createElement(Form, _extends({}, formProps, {
      actions: false,
      enableDebounce: true
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'nowrap'
      }
    }, l2Search && !searchLifted && /*#__PURE__*/React.createElement("div", {
      style: {
        width: 260,
        maxWidth: '100%'
      }
    }, l2Search), l2Search && searchLifted && /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'none'
      }
    }, l2Search), !titleAndToolbars && l2Fields.length > 0 && /*#__PURE__*/React.createElement(FilterPopover, {
      count: filterCount,
      label: t('GENERAL.filters'),
      fields: l2Fields,
      onReset: () => setTerm('')
    }), frameworkActions.length > 0 && /*#__PURE__*/React.createElement(OverflowActions, null, frameworkActions), toolbarActionsContent)));
  }
  return /*#__PURE__*/React.createElement(Form, _extends({}, formProps, {
    actions: false,
    enableDebounce: true
  }), (title || entity || titleAndToolbars || showSearchField || toolbarActionsContent) && /*#__PURE__*/React.createElement(FlexRow, {
    alignItems: "center",
    paddingRight: 4,
    paddingLeft: 4
  }, !shouldHideTitle && (title || entity) && /*#__PURE__*/React.createElement(LargeText, {
    width: "auto",
    textAlign: "left",
    margin: 0,
    marginTop: 4
  }, title || t(`${entity}.titlePlural`)), (titleAndToolbars || showSearchField) && /*#__PURE__*/React.createElement(Block, {
    width: "28%",
    marginBottom: 0,
    marginTop: 0,
    marginLeft: "auto",
    key: "search"
  }, searchField), toolbarActionsContent && /*#__PURE__*/React.createElement(Block, {
    width: "auto",
    marginLeft: !showSearchField && 'auto',
    margin: 0,
    marginTop: 4
  }, toolbarActionsContent)), content);
});
ContainerHeaderCustom.propTypes = {
  entity: PropTypes.string,
  filtersContent: PropTypes.any,
  toolbarActionsContent: PropTypes.any,
  onFilterFormChange: PropTypes.func,
  onAiClick: PropTypes.func
};
const ContainerHeader = /*#__PURE__*/React.memo(ContainerHeaderCustom);
export default ContainerHeader;